import { defineStore } from 'pinia';
import { createTenantDocument, deleteTenantDocument, fetchTenantCollection, updateTenantDocument } from '~/lib/tenantCollection';
import { stripUndefined } from '~/lib/stripUndefined';
import { toMillis } from '~/lib/timestamps';
import type { ContractFieldDefinition, ContractTableDefinition, ContractTemplate } from '~/types/models';

type TemplateInput = { title: string; contentHtml: string; fields: ContractFieldDefinition[]; tables?: ContractTableDefinition[] };

export const useContractTemplatesStore = defineStore('contract-templates', {
    state: () => ({ templates: [] as ContractTemplate[], loadedTenantId: '' }),
    actions: {
        async fetchAll() {
            const tenantId = useTenantStore().currentTenantId;
            if (!tenantId) return;
            const { $db } = useNuxtApp();
            const templates = await fetchTenantCollection<ContractTemplate>($db, tenantId, 'contractTemplates');
            this.templates = templates.sort((a, b) => toMillis(b.updatedAt) - toMillis(a.updatedAt));
            this.loadedTenantId = tenantId;
        },
        async save(input: TemplateInput, id?: string) {
            const tenantId = useTenantStore().currentTenantId;
            const actorId = useAuthStore().user?.uid;
            if (!tenantId || !actorId) throw new Error('Sign in to save a contract template.');
            const { $db } = useNuxtApp();
            const now = new Date();
            const payload = stripUndefined({ title: input.title.trim(), contentHtml: input.contentHtml, fields: input.fields, tables: input.tables || [], updatedAt: now });
            if (!payload.title || !payload.contentHtml) throw new Error('Add a title and contract content.');
            if (id) {
                await updateTenantDocument($db, tenantId, 'contractTemplates', id, payload);
                this.templates = this.templates.map(template => template.id === id ? { ...template, ...payload } as ContractTemplate : template);
                return id;
            }
            const created = { ...payload, tenantId, createdBy: actorId, createdAt: now };
            const newId = await createTenantDocument($db, tenantId, 'contractTemplates', created);
            this.templates.unshift({ id: newId, ...created } as ContractTemplate);
            return newId;
        },
        async remove(id: string) {
            const tenantId = useTenantStore().currentTenantId;
            if (!tenantId) throw new Error('No workspace selected.');
            const { $db } = useNuxtApp();
            await deleteTenantDocument($db, tenantId, 'contractTemplates', id);
            this.templates = this.templates.filter(template => template.id !== id);
        },
    },
});
